import { Link } from "react-router-dom";
import Lottie from "lottie-react";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { 
  Gear, 
  Coins, 
  Users, 
  ArrowRight, 
  Sparkle, 
  CheckCircle, 
  Lightning, 
  ShieldCheck 
} from "@phosphor-icons/react";
import hrPromoAnimation from "../assets/animations/hr-promo.json";

const HrPromoSection = () => {
  const { user } = useAuth();

  const features = [
    {
      icon: <Gear className="w-6 h-6 sm:w-7 sm:h-7 text-visaro-orange group-hover:scale-110 transition-transform duration-300" weight="duotone" />,
      tag: "0% Platform Fee",
      title: "Free HR Tools",
      description: "Manage onboarding, payroll, and core HR operations with zero subscription or platform fees.",
    },
    {
      icon: <Coins className="w-6 h-6 sm:w-7 sm:h-7 text-visaro-orange group-hover:scale-110 transition-transform duration-300" weight="duotone" />,
      tag: "Instant Payouts",
      title: "Credit on Request",
      description: "Offer your employees instant access to salary advances and financial flexibility managed from your dashboard.",
    },
    {
      icon: <Users className="w-6 h-6 sm:w-7 sm:h-7 text-visaro-orange group-hover:scale-110 transition-transform duration-300" weight="duotone" />,
      tag: "Automated Suite",
      title: "Employee Management",
      description: "Track attendance, employee records, and streamline workforce operations effortlessly in one portal.",
    },
  ];

  return (
    <section className="relative overflow-hidden  via-white to-gray-50 py-10 md:py-20 px-4 sm:px-8 md:px-16 lg:px-24 text-gray-800 border-y border-gray-200/60">
      {/* Background Soft Glow Orbs matching Visaro theme */}
      <div className="absolute top-10 left-1/3 w-80 h-80 bg-visaro-orange/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-10 right-1/3 w-80 h-80 bg-visaro-visaGreen/15 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto flex flex-col gap-10 md:gap-16 relative z-10">
        
        {/* Main Banner Card */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-gray-200/80 shadow-lg sm:shadow-xl p-5 sm:p-8 md:p-12 lg:p-16 relative overflow-hidden">
          {/* Top Decorative Accent Line */}
          {/* <div className="absolute top-0 left-0 right-0 h-1.5 sm:h-2 bg-gradient-to-r from-visaro-orange via-visaro-orange to-visaro-visaGreen" /> */}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
            
            {/* Left Column Text Content */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="lg:col-span-7 flex flex-col gap-4 sm:gap-6"
            >
              {/* Feature Release Badge */}
              <div className="inline-flex items-center gap-2 rounded-full  px-3.5 py-1 sm:px-4 sm:py-1.5 text-xs md:text-sm font-bold text-visaro-orange border border-visaro-orange/30 w-fit">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-visaro-orange opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-visaro-orange"></span>
                </span>
                <Sparkle weight="fill" className="w-3.5 h-3.5 text-visaro-orange" />
                <span>NEW FEATURE RELEASE</span>
              </div>
              
              {/* Main Headline */}
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold font-aeonic text-deepBlue leading-tight tracking-tight">
                Empower Your Workforce. <br className="hidden sm:block" />
                <span className="text-visaro-orange">Manage HR for Free.</span>
              </h2>
              
              {/* Sub-headline */}
              <p className="text-sm sm:text-base md:text-xl text-gray-600 leading-relaxed font-aeonic">
                Running a company of 100+ employees? Visaro now provides a complete, free Human Resources Management system built right into our platform.
              </p>

              {/* The "Hook" Highlight Box */}
              <div className="rounded-xl sm:rounded-2xl  border border-visaro-orange/20 p-4 sm:p-5 md:p-6 flex items-start gap-3 sm:gap-4">
                <div className="p-2 sm:p-2.5 rounded-lg sm:rounded-xl bg-visaro-orange text-white shrink-0 shadow-sm mt-0.5">
                  <Lightning weight="fill" className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <div>
                  <h4 className="text-sm sm:text-base font-bold text-deepBlue mb-0.5 sm:mb-1 font-aeonic">
                    Go Beyond Basic HR
                  </h4>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed">
                    Give your team access to instant <span className="font-semibold text-deepBlue">Credit on Request</span>, salary advances, and seamless financial flexibility—managed effortlessly from your admin dashboard.
                  </p>
                </div>
              </div>

              {/* CTA Row */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                <Link
                  to="/survey"
                  className="group inline-flex items-center justify-center gap-2.5 px-6 sm:px-8 py-3.5 sm:py-4 text-sm sm:text-base font-bold rounded-xl text-white bg-deepBlue hover:bg-opacity-95 transition-all shadow-md hover:shadow-lg w-full sm:w-auto text-center"
                >
                  <span>{user ? "Upgrade to Company Account" : "Claim Your Free HR Portal"}</span>
                  <ArrowRight weight="bold" className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                </Link>

                <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500 font-medium pt-1 sm:pt-0">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle weight="fill" className="text-visaro-visaGreen w-4 h-4 sm:w-5 sm:h-5 shrink-0" /> 100% Free
                  </span>
                  <span className="text-gray-300">•</span>
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck weight="fill" className="text-visaro-visaGreen w-4 h-4 sm:w-5 sm:h-5 shrink-0" /> Instant Setup
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Right Column Illustration & Floating Interactive Badges */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
              className="lg:col-span-5 relative flex justify-center items-center mt-4 lg:mt-0"
            >
              {/* Soft Aura Ring */}
              <div className="absolute inset-0 bg-visaro-orange/10 rounded-2xl sm:rounded-3xl blur-xl transform scale-95" />

              <div className="relative w-full max-w-xs sm:max-w-md aspect-square rounded-2xl sm:rounded-3xl bg-gray-50 border border-gray-200/80 p-4 sm:p-6 shadow-inner flex items-center justify-center overflow-hidden">
                <Lottie 
                  animationData={hrPromoAnimation} 
                  loop={true} 
                  className="w-full h-full object-contain" 
                />

                {/* Floating Badge Top-Right */}
                <motion.div 
                  animate={{ y: [0, -4, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-white/95 backdrop-blur-md border border-gray-200 shadow-md px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl sm:rounded-2xl flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs scale-90 sm:scale-100 origin-top-right"
                >
                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl bg-visaro-visaGreen/20 text-visaro-visaGreen flex items-center justify-center font-bold shrink-0">
                    ✓
                  </div>
                  <div>
                    <div className="text-[9px] sm:text-[10px] text-gray-400 font-normal">Salary Advance</div>
                    <div className="text-deepBlue font-bold">Approved Instantly</div>
                  </div>
                </motion.div>

                {/* Floating Badge Bottom-Left */}
                <motion.div 
                  animate={{ y: [0, 4, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 bg-white/95 backdrop-blur-md border border-gray-200 shadow-md px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl sm:rounded-2xl flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs scale-90 sm:scale-100 origin-bottom-left"
                >
                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl bg-visaro-orange/20 text-visaro-orange flex items-center justify-center font-bold shrink-0">
                    👥
                  </div>
                  <div>
                    <div className="text-[9px] sm:text-[10px] text-gray-400 font-normal">Free Portal</div>
                    <div className="text-visaro-orange font-bold">100+ Employees</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

          </div>
        </div>

        {/* 3-Column Feature Mini-Grid Banner */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8"
        >
          {features.map((feature, idx) => (
            <div 
              key={idx} 
              className="group bg-white border border-gray-200/90 rounded-2xl sm:rounded-3xl p-6 sm:p-8 hover:border-visaro-orange/50 hover:shadow-lg transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="p-3 sm:p-3.5 rounded-xl sm:rounded-2xl bg-visaro-orange-15 text-visaro-orange border border-visaro-orange/20 group-hover:bg-visaro-orange group-hover:text-white transition-colors duration-300 shadow-sm">
                    {feature.icon}
                  </div>
                  <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200 group-hover:bg-visaro-orange-15 group-hover:text-visaro-orange group-hover:border-visaro-orange/30 transition-colors">
                    {feature.tag}
                  </span>
                </div>

                <h3 className="text-lg sm:text-xl font-bold text-deepBlue mb-1.5 sm:mb-2 font-aeonic group-hover:text-visaro-orange transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-500 text-xs sm:text-sm md:text-base leading-relaxed">
                  {feature.description}
                </p>
              </div>

              <div className="mt-6 sm:mt-8 pt-3 sm:pt-4 border-t border-gray-100 flex items-center text-xs font-bold text-visaro-orange">
                <span className="flex items-center gap-1.5">
                  Learn more <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default HrPromoSection;
